import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <Section ref={sectionRef}>
      <BackgroundPattern />

      <Container>
        <ContentWrapper $isVisible={isVisible}>
          <ContentLeft>
            <Badge $isVisible={isVisible}>
              <BadgeDot />
              Introducing the Future
            </Badge>

            <Heading $isVisible={isVisible}>
              Mission: G.A.I.A. <br />
              <HeadingHighlight>EDTech Platform</HeadingHighlight>
            </Heading>

            <Subheading $isVisible={isVisible}>
              Welcome to Mission: G.A.I.A.—the Global Anticipatory Intelligence Agency—where immersive storytelling and hands-on challenges empower the next generation of environmental leaders to shape a resilient future.
            </Subheading>

            <ButtonGroup $isVisible={isVisible}>
              <PrimaryButton href="#about">
                Learn More
                <ArrowIcon>→</ArrowIcon>
              </PrimaryButton>
              {/* <SecondaryButton href="#about">
                Learn More
              </SecondaryButton> */}
            </ButtonGroup>

            {/* <MetricsRow $isVisible={isVisible}>
              <Metric>
                <MetricValue>9-12</MetricValue>
                <MetricLabel>Age Group</MetricLabel>
              </Metric>
              <MetricDivider />
              <Metric>
                <MetricValue>UNESCO</MetricValue>
                <MetricLabel>Partner</MetricLabel>
              </Metric>
              <MetricDivider />
              <Metric>
                <MetricValue>Award</MetricValue>
                <MetricLabel>Winning Books</MetricLabel>
              </Metric>
            </MetricsRow> */}
          </ContentLeft>

          <ContentRight $isVisible={isVisible}>
            <ImageCard>
              <ImageWrapper>
                <BookImage src="/Future+Hack_Cover.webp" alt="Future Hack Book Cover" />
              </ImageWrapper>
              <ImageAccent />
            </ImageCard>

            <FeatureCards>
              <FeatureCard $delay="0.3s" $isVisible={isVisible}>
                <FeatureIcon>📖</FeatureIcon>
                <FeatureTitle>Story-Driven</FeatureTitle>
              </FeatureCard>
              <FeatureCard $delay="0.4s" $isVisible={isVisible}>
                <FeatureIcon>🎮</FeatureIcon>
                <FeatureTitle>Gamified</FeatureTitle>
              </FeatureCard>
              <FeatureCard $delay="0.5s" $isVisible={isVisible}>
                <FeatureIcon>🌍</FeatureIcon>
                <FeatureTitle>Interactive</FeatureTitle>
              </FeatureCard>
            </FeatureCards>
          </ContentRight>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled Components
const Section = styled.section`
  position: relative;
  width: 100vw;
//   min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%);
  // background: linear-gradient(120deg, #eaf1fb 0%, #e3eafc 40%, #dbeafe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 150px 0; 
  overflow: hidden;
  z-index: 0;

  @media (max-width: 1600px) {
    padding: 130px 0 80px;
  }

  @media (max-width: 1440px) {
    padding: 110px 0 70px;
  }

  @media (max-width: 1280px) {
    padding: 100px 0 60px;
  }

  @media (max-width: 1024px) {
    padding: 90px 0 60px;
    min-height: auto;
  }

  @media (max-width: 900px) {
    padding: 80px 0 50px;
  }

  @media (max-width: 768px) {
    padding: 70px 0 40px;
  }

  @media (max-width: 640px) {
    padding: 60px 0 40px;
  }

  @media (max-width: 480px) {
    padding: 50px 0 35px;
  }

  @media (max-width: 375px) {
    padding: 45px 0 30px;
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  background-image:
    radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(96, 165, 250, 0.3) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 80px;
  position: relative;
  z-index: 1;

  @media (max-width: 1600px) {
    padding: 0 70px;
  }

  @media (max-width: 1440px) {
    padding: 0 60px;
  }

  @media (max-width: 1280px) {
    padding: 0 50px;
  }

  @media (max-width: 1024px) {
    padding: 0 40px;
  }

  @media (max-width: 900px) {
    padding: 0 32px;
  }

  @media (max-width: 768px) {
    padding: 0 28px;
  }

  @media (max-width: 640px) {
    padding: 0 24px;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
  }

  @media (max-width: 375px) {
    padding: 0 16px;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeIn : 'none'} 0.8s ease-out forwards;

  @media (max-width: 1440px) {
    gap: 70px;
  }

  @media (max-width: 1280px) {
    gap: 60px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }

  @media (max-width: 900px) {
    gap: 45px;
  }

  @media (max-width: 768px) {
    gap: 40px;
  }

  @media (max-width: 640px) {
    gap: 35px;
  }

  @media (max-width: 480px) {
    gap: 30px;
  }

  @media (max-width: 375px) {
    gap: 25px;
  }
`;

const ContentLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 1280px) {
    gap: 30px;
  }

  @media (max-width: 1024px) {
    gap: 28px;
    align-items: center;
    text-align: center;
  }

  @media (max-width: 900px) {
    gap: 26px;
  }

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 640px) {
    gap: 22px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }

  @media (max-width: 375px) {
    gap: 18px;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  font-size: 12px;
  font-weight: 600;
  color: #60a5fa;
  letter-spacing: 0.5px;
  width: fit-content;
  backdrop-filter: blur(10px);
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeInUp : 'none'} 0.6s ease-out 0.1s forwards;

  @media (max-width: 1024px) {
    font-size: 11.5px;
    padding: 9px 17px;
  }

  @media (max-width: 900px) {
    font-size: 11px;
    padding: 8.5px 16px;
  }

  @media (max-width: 768px) {
    font-size: 10.5px;
    padding: 8px 15px;
  }

  @media (max-width: 640px) {
    font-size: 10px;
    padding: 7.5px 14px;
  }

  @media (max-width: 480px) {
    font-size: 9.5px;
    padding: 7px 13px;
  }

  @media (max-width: 375px) {
    font-size: 9px;
    padding: 6px 12px;
  }
`;

const BadgeDot = styled.span`
  width: 6px;
  height: 6px;
  background: #60a5fa;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(96, 165, 250, 0.6);

  @media (max-width: 480px) {
    width: 4px;
    height: 4px;
  }
`;

const Heading = styled.h1`
  font-size: 68px;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.02em;
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeInUp : 'none'} 0.6s ease-out 0.2s forwards;

  @media (max-width: 1600px) {
    font-size: 64px;
  }

  @media (max-width: 1440px) {
    font-size: 58px;
  }

  @media (max-width: 1280px) {
    font-size: 54px;
  }

  @media (max-width: 1024px) {
    font-size: 50px;
  }

  @media (max-width: 900px) {
    font-size: 46px;
  }

  @media (max-width: 768px) {
    font-size: 42px;
  }

  @media (max-width: 640px) {
    font-size: 38px;
  }

  @media (max-width: 480px) {
    font-size: 34px;
  }

  @media (max-width: 375px) {
    font-size: 30px;
  }

  @media (max-width: 320px) {
    font-size: 26px;
  }
`;

const HeadingHighlight = styled.span`
  color: #60a5fa;
`;

const Subheading = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #cbd5e1;
  margin: 0;
  max-width: 600px;
  font-weight: 400;
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeInUp : 'none'} 0.6s ease-out 0.3s forwards;

  @media (max-width: 1600px) {
    font-size: 18px;
    max-width: 580px;
  }

  @media (max-width: 1440px) {
    font-size: 17.5px;
    max-width: 560px;
  }

  @media (max-width: 1280px) {
    font-size: 17px;
    max-width: 540px;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    font-size: 16.5px;
  }

  @media (max-width: 900px) {
    font-size: 16px;
    line-height: 1.65;
  }

  @media (max-width: 768px) {
    font-size: 15.5px;
    line-height: 1.6;
  }

  @media (max-width: 640px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14.5px;
  }

  @media (max-width: 375px) {
    font-size: 14px;
  }

  @media (max-width: 320px) {
    font-size: 13px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeInUp : 'none'} 0.6s ease-out 0.4s forwards;

  @media (max-width: 480px) {
    gap: 12px;
    width: 100%;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);

  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 14px 28px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 12px 24px;
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 375px) {
    font-size: 13px;
    padding: 11px 20px;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  background: transparent;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 14px 28px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 12px 24px;
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 375px) {
    font-size: 13px;
    padding: 11px 20px;
  }
`;

const ArrowIcon = styled.span`
  font-size: 18px;
  transition: transform 0.3s ease;

  ${PrimaryButton}:hover & {
    transform: translateX(4px);
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const MetricsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  padding-top: 8px;
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeInUp : 'none'} 0.6s ease-out 0.5s forwards;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }

  @media (max-width: 375px) {
    gap: 16px;
  }
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }

  @media (max-width: 375px) {
    font-size: 14px;
  }
`;

const MetricLabel = styled.div`
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }

  @media (max-width: 375px) {
    font-size: 10px;
  }
`;

const MetricDivider = styled.div`
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);

  @media (max-width: 480px) {
    height: 28px;
  }

  @media (max-width: 375px) {
    height: 24px;
  }
`;

const ContentRight = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? slideInRight : 'none'} 0.8s ease-out 0.4s forwards;

  @media (max-width: 1024px) {
    justify-content: center;
    // order: -1;
  }
`;

const ImageCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;

  @media (max-width: 1600px) {
    max-width: 390px;
  }

  @media (max-width: 1440px) {
    max-width: 370px;
  }

  @media (max-width: 1280px) {
    max-width: 350px;
  }

  @media (max-width: 1024px) {
    max-width: 400px;
    margin: 0 auto;
  }

  @media (max-width: 900px) {
    max-width: 380px;
  }

  @media (max-width: 768px) {
    max-width: 350px;
  }

  @media (max-width: 640px) {
    max-width: 320px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-8px);
  }

  @media (max-width: 768px) {
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    border-radius: 12px;
  }
`;

const BookImage = styled.img`
  width: 100%;
//   height: auto;
  display: block;
`;

const ImageAccent = styled.div`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 160px;
  height: 160px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(96, 165, 250, 0.3));
  border-radius: 50%;
  filter: blur(40px);
  pointer-events: none;
  z-index: -1;

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    top: -15px;
    right: -15px;
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
    top: -10px;
    right: -10px;
  }
`;

const FeatureCards = styled.div`
  position: absolute;
  bottom: -24px;
  left: -24px;
  display: flex;
  gap: 12px;

  @media (max-width: 1024px) {
    position: relative;
    bottom: auto;
    left: auto;
    margin-top: 24px;
    justify-content: center;
  }

  @media (max-width: 480px) {
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 20px;
  }
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.$isVisible ? 1 : 0};
  animation: ${props => props.$isVisible ? fadeInUp : 'none'} 0.5s ease-out ${props => props.$delay}s forwards;

  @media (max-width: 768px) {
    padding: 10px 14px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    border-radius: 8px;
  }
`;

const FeatureIcon = styled.span`
  font-size: 20px;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const FeatureTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }

  @media (max-width: 375px) {
    font-size: 11px;
  }
`;

export default HeroSection;